<template>
  <div
    ref="trigger"
    :class="[
      !props.useCustomTemplate && 'calendar',
      !props.useCustomTemplate && isShowDatePicker && 'calendar--active'
    ]"
    @click="toggleDatePicker"
  >
    <template v-if="props.useCustomTemplate">
      <slot />
    </template>
    <template v-else>
      <div class="calendar__date">
        {{ formattedStart }}
      </div>
      <div class="calendar__date">
        {{ formattedEnd }}
      </div>
    </template>

    <Teleport to="body">
      <DatePicker
        v-if="isShowDatePicker"
        :model-value="modelValue"
        :position="pickerPosition"
        :active-calendar-type="activeCalendarType"
        :locale-messages="localeMessages"
        :use-confirmation="useConfirmation"
        :max-date="maxDate"
        @update:model-value="args => emit('update:modelValue', args)"
        @hide-date-picker="hideDatePicker"
        @click.stop
      />
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount, defineProps, defineEmits } from "vue";
import DatePicker from "@/components/DatePicker/DatePicker.vue";
import { useClickOutsideCalendar } from "@/components/DatePicker/useClickOutsideCalendar";

// Импорт локализации.
import ru from "@/components/DatePicker/locales/ru";
import en from "@/components/DatePicker/locales/en";
import es from "@/components/DatePicker/locales/es";
import fr from "@/components/DatePicker/locales/fr";

const localeList = {
  ru: ru,
  en: en,
  es: es,
  fr: fr
};

const props = defineProps({
  modelValue: {
    type: [Array, null],
    required: true,
    default: () => [new Date(), new Date()]
  },
  /** Использование кастомного тригера. */
  useCustomTemplate: {
    type: Boolean,
    default: false
  },
  /**
   * Расположение календаря относительно триггера
   *          bottom-left, bottom-center, bottom-right,
   *          top-left, top-center, top-right,
   *          left, right
   */
  placement: {
    type: String,
    default: "bottom-left",
  },
  /** Активный язык */
  locale: {
    type: String,
    default: "ru"
  },
  /** Использование подтверждения и отображения активной даты  */
  useConfirmation: {
    type: Boolean,
    default: false
  },
  /** До какого дня разрешено выбирать дату */
  maxDate: {
    type: [Date, null],
    default: () => new Date()
  }
});

const emit = defineEmits(["update:modelValue"]);

const trigger          = ref(null);
const isShowDatePicker = ref(false);
const pickerPosition   = ref({ top: 0, left: 0 });

// Для доступа к переменной видимости календаря.
defineExpose({isShowDatePicker});

// Клик вне календаря
useClickOutsideCalendar(trigger, () => {
  isShowDatePicker.value = false;
});

// Рассчет координат открытия календаря
function updatePickerPosition() {
  const t = trigger.value;
  if (!t) return;

  const rect    = t.getBoundingClientRect();
  const scrollY = window.scrollY;
  const scrollX = window.scrollX;

  const offset = 8;
  const pickerEl = document.querySelector(".date-picker");
  const pw = pickerEl?.offsetWidth  || 0;
  const ph = pickerEl?.offsetHeight || 0;

  let top = 0;
  let left = 0;

  switch (props.placement) {
    case "bottom-left":
      top = rect.bottom + scrollY + offset;
      left = rect.left   + scrollX;
      break;
    case "bottom-center":
      top = rect.bottom + scrollY + offset;
      left = rect.left + scrollX + rect.width / 2 - pw / 2;
      break;
    case "bottom-right":
      top = rect.bottom + scrollY + offset;
      left = rect.right  + scrollX - pw;
      break;
    case "top-left":
      top = rect.top    + scrollY - ph - offset;
      left = rect.left   + scrollX;
      break;
    case "top-center":
      top = rect.top    + scrollY - ph - offset;
      left = rect.left + scrollX + rect.width / 2 - pw / 2;
      break;
    case "top-right":
      top = rect.top    + scrollY - ph - offset;
      left = rect.right  + scrollX - pw;
      break;
    case "left":
      top = rect.top + scrollY + rect.height / 2 - ph / 2;
      left = rect.left + scrollX - pw - offset;
      break;
    case "right":
      top = rect.top + scrollY + rect.height / 2 - ph / 2;
      left = rect.right + scrollX + offset;
      break;
    default:
      // По умолчанию нижний левый
      top = rect.bottom + scrollY + offset;
      left = rect.left + scrollX;
  }

  pickerPosition.value = { top, left };
}

function showDatePicker() {
  isShowDatePicker.value = true;
  nextTick(updatePickerPosition);
}
function hideDatePicker() {
  isShowDatePicker.value = false;
}
function toggleDatePicker() {
  isShowDatePicker.value ? hideDatePicker() : showDatePicker();
}

// Пересчёт на скролл/ресайз, подключение локализации.

onMounted(() => {
  // Загрузка локализации. При необходимости можно подключить через проп.
  // При подключение через проп изменить функцию formatShort
  loadLocale(props.locale);
  // loadLocale(language.value);

  window.addEventListener("scroll", updatePickerPosition);
  window.addEventListener("resize", updatePickerPosition);
});
onBeforeUnmount(() => {
  window.removeEventListener("scroll", updatePickerPosition);
  window.removeEventListener("resize", updatePickerPosition);
});

// Тип активного календаря.
const activeCalendarType = "range";

// Форматирование даты к виду "22 сент., пн" в зависимости от языка
function formatShort(d) {
  if (!d) return "";

  const options = {
    day: "numeric",
    month: "short",
  };

  const dayMonth = d.toLocaleDateString(props.locale, options).replace(/\./g, "");
  const weekday = d.toLocaleDateString(props.locale, { weekday: "short" }).replace(/\./g, "");

  return `${dayMonth}, ${weekday}`;
}

// Вывод даты в виде: "вт, 1 июл." в зависимости от языка
const formattedStart = computed(() => formatShort(props.modelValue[0]));
const formattedEnd   = computed(() => formatShort(props.modelValue[1]));

// Данные для локализации
const localeMessages = ref({
  calendar: {
    months: { long: [], short: [] },
    weekdays: { long: [], short: [] },
    buttons: {}
  }
});

// Загрузка файла локализации
async function loadLocale(lang) {
  try {
    localeMessages.value = localeList[lang];
  } catch (e) {
    console.error(`Не удалось загрузить локаль ${lang}`, e);
  }
}
</script>

<style scoped lang="scss">
.calendar {
  background: #F7F8FD;
  border-radius: 8px;
  box-sizing: border-box;
  display: flex;
  gap: 4px;
  padding: 6px;
  position: relative;
  min-width: 0;
  width: 100%;

  &:hover {
    background: #EEF1F4;
  }

  &__date {
    background: #FFF;
    border-radius: 4px;
    color: #898989;
    display: inline;
    font-size: 12px;
    font-weight: 400;
    overflow: hidden;
    padding: 4px 6px;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
    text-align: center;
  }

  &--active { background: #E0F0FF; }
  &--active:hover { background: #E0F0FF; }
}
</style>
